import { useCallback, useEffect, useRef, useState } from "react";
import { runInAction } from "mobx";
import { toast } from "react-toastify";
import { getConfig } from "../lib/configMgr";
import Analytics from "../lib/Analytics";

const { ExternalRESTAPI } = getConfig();

// How long to wait for the first byte before treating the connection as blocked
const CONNECT_TIMEOUT_MS = 10 * 1000;
// How long to wait between chunks before treating the connection as silently dead.
// Server pings every 10s; 30s = 3 missed pings. Catches corporate/mobile proxies
// that hold the TCP connection open but stop forwarding data mid-stream.
const INTER_CHUNK_TIMEOUT_MS = 30 * 1000;
// How often to poll in fallback mode — match server ping interval so we're no
// faster than SSE would be, without hammering the server unnecessarily.
export const POLL_INTERVAL_MS = 10 * 1000;
// Timeout for individual poll requests — prevents a hung proxy from stalling the loop
const POLL_REQUEST_TIMEOUT_MS = 15000;
// Backstop interval for the SSE recovery probe while in polling mode. This only
// fires the probe as a last resort — the primary trigger is event-driven (see
// startRecovery): `online` events and, where supported, Network Information
// API `connection.change` events. A fixed short interval caused repeated
// "switched to compatibility mode" / "restored" toast ping-pong for proxies
// that intermittently allow/block SSE with no actual network change (e.g.
// Novartis corporate proxy) — the probe kept re-checking on a timer even
// though nothing had changed. 5 minutes is just a safety net for browsers
// without the Network Information API (Safari) that also never fire `online`
// (e.g. connectivity never actually dropped, just the proxy's SSE behavior
// changed) — see startRecovery for the event-driven path.
const SSE_RECOVERY_BACKSTOP_INTERVAL_MS = 5 * 60 * 1000;
// How long the recovery probe waits for a response
const PROBE_TIMEOUT_MS = 5000;
// How long to cache polling-mode state in sessionStorage before rechecking
const POLLING_MODE_TTL_MS = 60 * 1000;
// Max mid-stream reconnect attempts before surfacing the error
const MAX_RECONNECTS = 5;

// Capped exponential backoff: 1s, 2s, 4s, 5s, 5s, …
function reconnectBackoffMs(attempt) {
  return Math.min(1000 * Math.pow(2, attempt), 5000);
}

const POLLING_MODE_KEY = "sse_polling_mode";

function readPollingModeCache() {
  try {
    const raw = sessionStorage.getItem(POLLING_MODE_KEY);
    if (!raw) return false;
    const { value, ts } = JSON.parse(raw);
    if (Date.now() - ts > POLLING_MODE_TTL_MS) {
      sessionStorage.removeItem(POLLING_MODE_KEY);
      return false;
    }
    return value;
  } catch {
    return false;
  }
}

function writePollingModeCache(value) {
  try {
    sessionStorage.setItem(POLLING_MODE_KEY, JSON.stringify({ value, ts: Date.now() }));
  } catch {
    // sessionStorage unavailable (private browsing) — ignore
  }
}

function streamURL(streamId, mode) {
  const tpl = ExternalRESTAPI.Stream;
  const base = tpl.includes("{streamId}") ? tpl.replace("{streamId}", streamId) : `${tpl}?channel=${streamId}`;
  const sep = base.includes("?") ? "&" : "?";
  return mode ? `${base}${sep}mode=${mode}` : base;
}

function probeURL() {
  return ExternalRESTAPI.StreamProbe || "/api/stream/probe";
}

function authHeaders(auth) {
  return auth?.jwtToken ? { "Authorization": `Bearer ${auth.jwtToken}` } : {};
}

async function probeSSE(auth) {
  if (isSimulatingSSEBlock()) return false;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(probeURL(), {
      headers: { ...authHeaders(auth), "Accept": "text/event-stream" },
      signal: controller.signal,
    });
    return res.ok && res.headers.get("content-type")?.includes("text/event-stream");
  } catch {
    return false;
  } finally {
    clearTimeout(timeout);
  }
}

// Max events processed inside one runInAction before yielding to the browser.
// A single reader.read() chunk can contain many buffered SSE events if the
// connection was silently stalled and then resumes with backlog (e.g. a proxy
// holding data, or the server queuing pings/tool_call events during a long
// backend stall) — processing an unbounded burst synchronously in one
// runInAction can block the main thread long enough to trip Chrome's
// unresponsive-page detector, independent of how fast any single event is.
// Batching bounds the worst case regardless of burst size.
const EVENT_BATCH_SIZE = 25;

// How many delivered event ids a stream remembers, so an event that reaches the
// client twice is dispatched once (PLEX-6110). Ids are unique per event (a uuid4 per
// publish, backend flask_sse.Message), and the server's own replay dedupes on the
// same id. A duplicate comes from an overlapping poll or a reconnect replay, both
// far shorter than this; the oldest ids are forgotten first.
export const DELIVERED_IDS_CAP = 2000;

// PLEX-6110/WEBAPP-7G instrumentation: cap on the per-stream ring buffer of
// recent raw events (attached to deliveredIds so it threads through the
// existing param without touching every call site). Deliberately small and
// cheap (no JSON work) — just enough to show, if the duplicate-message_start
// check in LLMClient.js ever fires despite DELIVERED_IDS_CAP dedup (e.g. a
// backend full-queue resend larger than DELIVERED_IDS_CAP evicting an older-
// but-still-relevant id), whether the repeat id was seen and whether dedup
// caught it.
const RECENT_EVENTS_CAP = 30;

// Polled events carry data already parsed into an object, and only a string has a preview. Calling
// .slice() on the object threw inside the batch, before onEvent, so in polling mode no event was
// ever delivered and the watchdog fired (PLEX-6528, WEBAPP-3S).
const previewOf = (raw) => (typeof raw.data === "string" ? raw.data.slice(0, 160) : "");

export async function dispatchEventsInBatches(events, onEvent, lastEventIdRef, abortSignal, deliveredIds) {
  for (let i = 0; i < events.length; i += EVENT_BATCH_SIZE) {
    // The caller may abort (streamId changed, e.g. a new turn started) while this
    // loop is mid-burst across its setTimeout(0) yield points. Without this check,
    // batches from a stream the caller has already abandoned keep reaching onEvent
    // after the abort, misapplying events into whatever turn/stream is now live
    // (WEBAPP-7F/7G).
    if (abortSignal?.aborted) return;
    const batch = events.slice(i, i + EVENT_BATCH_SIZE);
    runInAction(() => {
      for (const raw of batch) {
        const recentEvents = deliveredIds?._recentEvents;
        // An event already delivered on this stream is skipped, and the cursor stays
        // where it is rather than moving back to it. A second message_start for the
        // same turn reads as a protocol error that closes the turn (PLEX-6110).
        if (raw.id && deliveredIds) {
          if (deliveredIds.has(raw.id)) {
            if (recentEvents) {
              recentEvents.push({ type: raw.type, id: raw.id, ts: Date.now(), dedupSkipped: true, preview: previewOf(raw) });
              if (recentEvents.length > RECENT_EVENTS_CAP) recentEvents.shift();
            }
            continue;
          }
          deliveredIds.add(raw.id);
          if (deliveredIds.size > DELIVERED_IDS_CAP) deliveredIds.delete(deliveredIds.values().next().value);
        }
        if (recentEvents) {
          recentEvents.push({ type: raw.type, id: raw.id, ts: Date.now(), dedupSkipped: false, preview: previewOf(raw) });
          if (recentEvents.length > RECENT_EVENTS_CAP) recentEvents.shift();
        }
        const e = makeEvent(raw);
        if (deliveredIds) {
          Object.defineProperty(e, "srcElement", { value: { getRecentEvents: () => (deliveredIds._recentEvents || []).slice() }, configurable: true });
        }
        if (raw.id) lastEventIdRef.current = raw.id;
        onEvent(e);
      }
    });
    if (i + EVENT_BATCH_SIZE < events.length) {
      // Yield to the browser (paint, input handling) between batches.
      await new Promise(resolve => setTimeout(resolve, 0));
    }
  }
}

// Parse SSE lines from a text chunk into event objects.
// Both pendingRef (incomplete line) and currentRef (in-progress event) must survive
// across reader.read() calls — if a chunk boundary falls between the "event:" line
// and the (potentially large) "data:" line, the event type would be lost without currentRef.
function parseSSEChunk(text, pendingRef, currentRef) {
  // PLEX-6483: this used to do `(pendingRef.current + text).split("\n")`
  // on the WHOLE accumulated buffer every call. For one very long SSE
  // line with no "\n" until the very end (a multi-MB JSON data: field --
  // exactly what a highly-connected compound search_results payload
  // looks like), that's an O(n^2) re-scan: the same already-known-
  // line-boundary-free bytes get re-split again on every chunk as more
  // data arrives (found via a Python benchmark hitting this same
  // /api/stream endpoint with requests.iter_lines(), which has the
  // identical anti-pattern -- a 13-39MB single-line SSE payload measured
  // 2s of real server time vs. 90-270s of client "read" time, entirely
  // spent re-scanning the client's own buffer).
  //
  // pendingRef.current is guaranteed to contain no "\n" (any line would
  // already have been extracted and consumed by a prior call -- it's
  // exactly the tail left over after the last `lines.pop()`), so a new
  // "\n" can only start at or after pendingRef.current.length. Search
  // with indexOf from that offset instead of materializing + splitting
  // the whole buffer into an array every call.
  const buffer = pendingRef.current + text;
  const events = [];
  let lineStart = 0;
  const searchFrom = pendingRef.current.length;
  let idx;
  // eslint-disable-next-line no-cond-assign
  while ((idx = buffer.indexOf("\n", Math.max(searchFrom, lineStart))) !== -1) {
    const line = buffer.slice(lineStart, idx);
    lineStart = idx + 1;
    if (line.startsWith("event:")) {
      currentRef.current.type = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      const chunk = line.slice(5).replace(/^ /, ""); // strip single leading space per SSE spec
      currentRef.current.data = currentRef.current.data === undefined ? chunk : currentRef.current.data + "\n" + chunk;
    } else if (line.startsWith("id:")) {
      currentRef.current.id = line.slice(3).trim();
    } else if (line === "") {
      if (currentRef.current.data !== undefined) {
        events.push({ ...currentRef.current });
      }
      currentRef.current = {};
    }
  }
  pendingRef.current = buffer.slice(lineStart); // last incomplete line
  return events;
}

function makeEvent(raw) {
  const type = raw.type || "message";
  const e = new Event(type);
  // Event.type is read-only, set other properties directly
  // Normalize data to string: SSE streaming delivers strings; polling delivers pre-parsed objects.
  // safeParseJSON uses JSON.parse() which stringifies objects to "[object Object]" → parse fails.
  const dataStr = raw.data == null ? "" : (typeof raw.data === "string" ? raw.data : JSON.stringify(raw.data));
  Object.defineProperty(e, "data", { value: dataStr, writable: false });
  Object.defineProperty(e, "lastEventId", { value: raw.id || "", writable: false });
  if (type === "message_stop" || type === "message_start") {
    console.debug(`[makeEvent] type=${type} dataStr=${dataStr.slice(0, 200)}`);
  }
  return e;
}

// Dev/test: set sessionStorage.simulate_sse_blocked = "1" to simulate a proxy
// blocking SSE connections (no response received — connection_timeout path).
function isSimulatingSSEBlock() {
  try { return sessionStorage.getItem("simulate_sse_blocked") === "1"; } catch { return false; }
}

// One fetch+read pass of the SSE stream.
// Returns "done" when the server closes normally, "network_error" on a mid-stream
// drop, "connect_timeout" when no first byte arrives, or "bad_response" on a
// non-SSE/non-2xx HTTP response.  Calls onEvent for each parsed SSE event and
// updates lastEventIdRef so callers can resume with Last-Event-ID on reconnect.
//
// reconnect=true: adds cache/connection headers so reconnect attempts bypass any
// HTTP cache and avoid reusing a pooled connection to a dying upstream (e.g.
// mid-deploy container replacement). No query param is added for this: the
// backend's strict_validation rejects unspec'd params with HTTP 400
// (WEBAPP-2/PLEX-6386), and Cache-Control: no-store + fetch cache:"no-store"
// already defeat caching without one.
async function connectStreamingOnce(url, auth, lastEventIdRef, onEvent, abortSignal, onFirstByte, reconnect = false, recordTick, deliveredIds) {
  const fetchUrl = url;
  const headers = {
    ...authHeaders(auth),
    "Accept": "text/event-stream",
    ...(lastEventIdRef.current ? { "Last-Event-ID": lastEventIdRef.current } : {}),
    ...(reconnect ? { "Cache-Control": "no-store", "Connection": "close" } : {}),
  };

  const controller = new AbortController();
  const onAbort = () => controller.abort();
  abortSignal.addEventListener("abort", onAbort);

  let firstByteReceived = false;
  const connectTimeout = setTimeout(() => {
    if (!firstByteReceived) controller.abort("connect_timeout");
  }, CONNECT_TIMEOUT_MS);

  try {
    const res = await fetch(fetchUrl, { headers, signal: controller.signal, ...(reconnect ? { cache: "no-store" } : {}) });
    if (!res.ok || !res.headers.get("content-type")?.includes("text/event-stream")) {
      clearTimeout(connectTimeout);
      abortSignal.removeEventListener("abort", onAbort);
      return { reason: "bad_response", status: res.status };
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    const pending = { current: "" };
    const current = { current: {} };

    const connectStartMs = Date.now();
    let iteration = 0;
    while (true) {
      // PLEX-6310: record BEFORE constructing the race, independent of console/Sentry
      // breadcrumbs (both were found unreliable/silent during a real incident this was
      // meant to explain). setTimeout below is what actually protects against a hang,
      // so this call itself can never be what's stuck — if a future timeout capture
      // shows a stale `lastArmedAt` with no matching `lastEventAt`/reconnect afterward,
      // the loop genuinely stopped iterating (not an instrumentation gap).
      recordTick?.({ phase: "awaiting_read", iteration: ++iteration, armedAt: Date.now() });
      // Race reader.read() against an inter-chunk timeout. Corporate/mobile proxies
      // sometimes hold the TCP connection open but stop forwarding data mid-stream;
      // without this, reader.read() hangs indefinitely and the client only finds out
      // via the 180s watchdog. A network_error here triggers reconnect with Last-Event-ID.
      const readWithTimeout = new Promise((resolve) => {
        const t = setTimeout(() => resolve({ timedOut: true }), INTER_CHUNK_TIMEOUT_MS);
        // On rejection (e.g. AbortError when the stream is aborted mid-read — see
        // WEBAPP-lockup), resolve with a terminal { done: true } shape instead of the
        // raw error. Resolving with the bare error meant `{ done, value } = chunk`
        // destructured to done=undefined (falsy), so `if (done) break` below never
        // fired — the loop called reader.read() again on an already-errored reader,
        // which rejected again immediately, forever, with no yield to the browser.
        // Before INTER_CHUNK_TIMEOUT_MS was introduced, a rejected reader.read() threw
        // directly into the outer try/catch and exited cleanly; this restores that.
        reader.read().then(r => { clearTimeout(t); resolve(r); })
          .catch(err => { clearTimeout(t); resolve({ done: true, error: err }); });
      });
      const chunk = await readWithTimeout;
      if (chunk.timedOut) {
        recordTick?.({ phase: "inter_chunk_timeout", iteration, at: Date.now() });
        reader.cancel();
        return { reason: "network_error", error: new Error("inter_chunk_timeout") };
      }
      const { done, value, error } = chunk;
      if (done) {
        recordTick?.({ phase: "read_done", iteration, at: Date.now(), hadError: !!error });
        if (error) return { reason: "network_error", error };
        break;
      }
      recordTick?.({ phase: "read_resolved", iteration, resolvedAt: Date.now() });
      // reader.read() can resolve with real data from the moment BEFORE an abort()
      // (streamId changed elsewhere — new turn/continuation started) reaches the
      // network layer. Without this check, an already-abandoned stream's trailing
      // chunk still gets parsed and dispatched into onEvent, landing in whatever
      // turn/stream is now live client-side (WEBAPP-7F/7G).
      if (abortSignal.aborted) {
        reader.cancel();
        return { reason: "aborted" };
      }
      if (!firstByteReceived) {
        firstByteReceived = true;
        clearTimeout(connectTimeout);
        onFirstByte?.(Date.now() - connectStartMs);
      }
      const events = parseSSEChunk(decoder.decode(value, { stream: true }), pending, current);
      recordTick?.({ phase: "dispatching", iteration, eventCount: events.length, at: Date.now() });
      // Batched runInAction: MobX still batches mutations within each batch (one
      // React render per batch, not per event), but a chunk containing an unusually
      // large backlog is split so the main thread yields between batches instead of
      // processing everything in one uninterruptible synchronous pass.
      await dispatchEventsInBatches(events, onEvent, lastEventIdRef, abortSignal, deliveredIds);
      recordTick?.({ phase: "dispatched", iteration, at: Date.now() });
    }
    clearTimeout(connectTimeout);
    abortSignal.removeEventListener("abort", onAbort);
    return { reason: "done" };
  } catch (err) {
    clearTimeout(connectTimeout);
    abortSignal.removeEventListener("abort", onAbort);
    const reason = err?.message === "connect_timeout" || controller.signal.reason === "connect_timeout"
      ? "connect_timeout"
      : "network_error";
    return { reason, error: err };
  }
}

// Streaming mode: fetch with ReadableStream, reconnecting up to MAX_RECONNECTS
// times on network_error before surfacing the error to the caller.
// authRef is a React ref so reconnect attempts always use the current token.
//
// inter_chunk_timeout handling: corporate proxies sometimes forward the initial
// SSE events then silently stop forwarding mid-stream without closing the
// connection. Each reconnect resumes from Last-Event-ID so no events are lost.
// Backoff is capped at 5s — reconnects are essentially periodic polling, and a
// short cap keeps latency low while the proxy is in a bad state.
async function connectStreaming(url, authRef, lastEventIdRef, onEvent, onError, onReconnecting, abortSignal, onSSESuccess, onInterChunkTimeout, recordTick, deliveredIds) {
  if (isSimulatingSSEBlock()) {
    onError({ type: "error", reason: "connect_timeout" });
    return;
  }

  for (let attempt = 0; attempt <= MAX_RECONNECTS; attempt++) {
    if (abortSignal.aborted) return;
    recordTick?.({ phase: "connect_attempt", attempt, at: Date.now() });

    const result = await connectStreamingOnce(url, authRef.current, lastEventIdRef, onEvent, abortSignal,
      attempt === 0 ? (connectMs) => Analytics.event(Analytics.EVENTS.STREAMING_CONNECTED, { connect_ms: connectMs }) : undefined,
      attempt > 0, recordTick, deliveredIds);

    if (result.reason === "done") {
      // SSE completed successfully — clear polling mode cache so the next
      // stream starts fresh rather than jumping straight to polling.
      onSSESuccess?.();
      return;
    }

    if (abortSignal.aborted) return;

    if (result.reason === "connect_timeout") {
      onError({ type: "error", reason: "connect_timeout" });
      return;
    }

    if (result.reason === "bad_response") {
      // On auth failure, attempt one token refresh then retry before giving up.
      if ((result.status === 401 || result.status === 403) && authRef.current?.refresh) {
        try {
          await authRef.current.refresh({ status: result.status });
        } catch {
          // Refresh failed — fall through to surface the original error
        }
        if (!abortSignal.aborted) {
          const retry = await connectStreamingOnce(url, authRef.current, lastEventIdRef, onEvent, abortSignal, undefined, true,
            undefined, deliveredIds);
          if (retry.reason === "done") return;
          if (abortSignal.aborted) return;
        }
      }
      onError({ type: "error", reason: "bad_response", status: result.status });
      return;
    }

    // network_error — retry up to MAX_RECONNECTS with backoff
    if (attempt < MAX_RECONNECTS) {
      const isInterChunk = result.error?.message === "inter_chunk_timeout";
      if (isInterChunk && attempt === 0) {
        Analytics.event(Analytics.EVENTS.STREAMING_INTER_CHUNK_TIMEOUT, {
          last_event_id: lastEventIdRef.current?.slice(0, 8) ?? null,
        });
        onInterChunkTimeout?.(lastEventIdRef.current);
      }
      const delay = reconnectBackoffMs(attempt);
      console.debug(`[sse] network_error on attempt ${attempt + 1}/${MAX_RECONNECTS}, retrying in ${delay}ms (lastEventId=${lastEventIdRef.current?.slice(0, 8)})`);
      onReconnecting({ attempt: attempt + 1, maxAttempts: MAX_RECONNECTS, delayMs: delay });
      await new Promise(resolve => setTimeout(resolve, delay));
    } else {
      onError({ type: "error", reason: "network_error", error: result.error });
    }
  }
}

// Polling mode: repeatedly GET /api/stream/{id}?mode=poll with Accept: application/json
async function pollOnce(url, auth, lastEventId, outerSignal) {
  const sep = url.includes("?") ? "&" : "?";
  const pollUrl = url + sep + "last_event_id=" + (lastEventId ? encodeURIComponent(lastEventId) : "");
  const timeoutSignal = AbortSignal.timeout(POLL_REQUEST_TIMEOUT_MS);
  const signal = outerSignal && AbortSignal.any
    ? AbortSignal.any([outerSignal, timeoutSignal])
    : timeoutSignal;
  const res = await fetch(pollUrl, {
    headers: { ...authHeaders(auth), "Accept": "application/json" },
    signal,
  });
  if (!res.ok) { const e = new Error(`Poll failed: ${res.status}`); e.status = res.status; throw e; }
  try {
    return await res.json(); // { events: [...], done: bool }
  } catch {
    // Non-JSON response (e.g. HTML error page during server restart) — treat as transient 503
    const e = new Error("Poll response not JSON");
    e.status = 503;
    throw e;
  }
}

export const useStreamConnector = ({ streamId, auth, onEvent, onError, onPollingModeChange, onInterChunkTimeout, checkAgentAlive }) => {
  const [pollingMode, setPollingMode] = useState(() => readPollingModeCache());
  const abortRef = useRef(null);
  const lastEventIdRef = useRef(null);
  // Ids already dispatched on the current stream, shared by SSE and polling (PLEX-6110).
  const deliveredIdsRef = useRef(null);
  const reconnectToastRef = useRef(null);
  const recoveryTimerRef = useRef(null);
  const recoveryListenersRef = useRef(null);
  const pollTimerRef = useRef(null);
  const pollAbortRef = useRef(null);
  const pollingModeRef = useRef(readPollingModeCache());
  const authRef = useRef(auth);
  const pollingStartRef = useRef(pollingModeRef.current ? Date.now() : null);
  const sseUrlRef = useRef(null);
  // PLEX-6310: read-loop health, updated directly from connectStreaming/
  // connectStreamingOnce on every iteration/attempt — independent of console.debug
  // and Sentry breadcrumbs, both confirmed unreliable during the incident this was
  // added to diagnose (console breadcrumb capture went dark for ~9 minutes of a real
  // session with no explanation). Read via getLoopHealth() at watchdog-fire time so
  // the next occurrence's Sentry capture shows exactly where the loop was stuck,
  // rather than relying on logging that may itself have stopped working.
  const loopHealthRef = useRef({ phase: "idle" });
  const recordTick = useCallback((tick) => { loopHealthRef.current = tick; }, []);
  const getLoopHealth = useCallback(() => ({ ...loopHealthRef.current, snapshotAt: Date.now() }), []);
  const pollUrlRef = useRef(null);
  // Recovery (attemptRecovery, defined below) needs to reopen the real SSE
  // connection on a successful probe, but is declared before startStreaming
  // exists. A ref sidesteps reordering the useCallback chain / a dependency cycle.
  const startStreamingRef = useRef(null);

  useEffect(() => { authRef.current = auth; }, [auth]);

  const setPollingModeWithRef = useCallback((val) => {
    pollingModeRef.current = val;
    writePollingModeCache(val);
    setPollingMode(val);
    onPollingModeChange?.(val);
  }, [onPollingModeChange]);

  const stopRecovery = useCallback(() => {
    if (recoveryTimerRef.current) {
      clearInterval(recoveryTimerRef.current);
      recoveryTimerRef.current = null;
    }
    if (recoveryListenersRef.current) {
      const { onOnline, onConnectionChange } = recoveryListenersRef.current;
      window.removeEventListener("online", onOnline);
      navigator.connection?.removeEventListener("change", onConnectionChange);
      recoveryListenersRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    // Also end a poll still waiting on its response. Clearing the timer alone let
    // that tick dispatch its events and schedule the next one, so after recovery
    // reopened SSE, polling kept running alongside it (PLEX-6110).
    pollAbortRef.current?.abort();
    pollAbortRef.current = null;
  }, []);

  const startRecovery = useCallback(() => {
    stopRecovery();

    // Runs the actual probe; used by both the event-driven triggers and the backstop timer.
    const attemptRecovery = async () => {
      const available = await probeSSE(authRef.current);
      if (available) {
        stopRecovery();
        // A successful probe only proves SSE is reachable again — it doesn't
        // itself reopen a stream. Previously this just flipped pollingMode to
        // false and left the existing poll loop as the only active transport,
        // with no code path back to real streaming; if that poll loop then hit
        // an ambiguous 404 the client silently sat idle until the 180s
        // conversation-level watchdog fired (WEBAPP-3S, distinct from the
        // hidden-tab/stale-event/disconnect-grace fixes already applied here).
        // Stop polling and actually reconnect SSE so a live transport exists.
        stopPolling();
        setPollingModeWithRef(false);
        Analytics.event(Analytics.EVENTS.STREAMING_RECOVERED, {
          polling_duration_ms: pollingStartRef.current ? Date.now() - pollingStartRef.current : null,
        });
        pollingStartRef.current = null;
        toast.info("Real-time streaming restored", { autoClose: 4000 });
        startStreamingRef.current?.();
      }
    };

    // Primary trigger: only re-probe when the network actually changes, rather
    // than on a fixed timer regardless of whether anything changed. This is what
    // was causing repeated fallback/recovery toast ping-pong on proxies (e.g.
    // Novartis) that intermittently allow/block SSE with no real network change —
    // the old 60s interval kept re-probing on a schedule unrelated to the cause.
    const onOnline = () => { attemptRecovery(); };
    window.addEventListener("online", onOnline);

    // Network Information API — Chrome/Edge only (absent in Safari/Firefox).
    // `network_effective_type` is already read from this same navigator.connection
    // object in Analytics.js, so this reuses the same browser capability.
    const onConnectionChange = () => { attemptRecovery(); };
    navigator.connection?.addEventListener("change", onConnectionChange);

    recoveryListenersRef.current = { onOnline, onConnectionChange };

    // Backstop: browsers without the Network Information API (Safari) may never
    // fire `connection.change`, and the underlying condition can change without
    // `navigator.onLine` ever flipping (the proxy's SSE-blocking behavior changes
    // while the network connection itself stays "up"). A long interval ensures
    // those users eventually get re-checked without reintroducing the ping-pong.
    recoveryTimerRef.current = setInterval(attemptRecovery, SSE_RECOVERY_BACKSTOP_INTERVAL_MS);
  }, [stopRecovery, setPollingModeWithRef, stopPolling]);

  const runPolling = useCallback((url, streamSignal) => {
    stopPolling();
    // Each poll loop has its own abort, so stopPolling() can end it without aborting
    // the stream's signal, which a reopened SSE connection goes on using.
    const loop = new AbortController();
    pollAbortRef.current = loop;
    if (streamSignal.aborted) loop.abort();
    else streamSignal.addEventListener("abort", () => loop.abort(), { once: true });
    const signal = loop.signal;
    let pollCount = 0;
    let pollStartTime = Date.now();
    // 404 is ambiguous: it means "channel gone", which happens both when the stream
    // finished cleanly and drained past STREAM_DONE_RETAIN_S, AND when a premature
    // disconnect's grace window (STREAM_DISCONNECT_GRACE_S) has lapsed while the
    // underlying LLM turn is still running (WEBAPP-3S). Ask the caller to check
    // agent-alive status before trusting a bare 404 as "done".
    const handle404 = async () => {
      const alive = await checkAgentAlive?.();
      if (alive) {
        console.debug(`[poll] 404 but agent still alive — retrying`);
        if (!signal.aborted) pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
      } else {
        console.debug(`[poll] stream expired (404), agent not alive — treating as done`);
      }
    };
    const tick = async () => {
      // Guard against a zombie loop: if this stream was aborted (streamId changed,
      // unmount, or a fresh poll loop started) while tick() was in-flight, the
      // pending setTimeout that would have been cleared by stopPolling() doesn't
      // exist yet — without this check the old tick reschedules itself forever,
      // dispatching duplicate events into the same onEvent callback alongside
      // whatever loop replaced it (WEBAPP-lockup).
      if (signal.aborted) {
        console.debug(`[poll] aborted — stopping poll loop`);
        return;
      }
      const tickStart = Date.now();
      try {
        const lastId = lastEventIdRef.current;
        const result = await pollOnce(url, authRef.current, lastId, signal);
        if (signal.aborted) return;
        const { events, done } = result;
        pollCount++;
        console.debug(`[poll] lastEventId=${lastId?.slice(0,8)} → events=${events.length} done=${done}`);
        if (pollCount % 10 === 0) {
          Analytics.event(Analytics.EVENTS.STREAMING_POLL_TICK, {
            poll_count: pollCount,
            events_returned: events.length,
            elapsed_ms: Date.now() - pollStartTime,
            request_ms: Date.now() - tickStart,
          });
        }
        await dispatchEventsInBatches(events, onEvent, lastEventIdRef, signal, deliveredIdsRef.current);
        if (done) {
          console.debug(`[poll] stream done — stopping poll loop`);
        } else if (!signal.aborted) {
          pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
        }
      } catch (err) {
        if (signal.aborted) return;
        const status = err?.status;
        console.debug(`[poll] error status=${status}`, err);
        if (status === 404) {
          await handle404();
        } else if (status && status >= 400 && status < 500) {
          // Other client error (auth, bad request) — fatal, stop polling
          onError(Object.assign(new Event("error"), { reason: "poll_error", error: err }));
        } else {
          // Transient (network, 5xx) — retry
          pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
        }
      }
    };
    tick();
  }, [onEvent, onError, stopPolling, checkAgentAlive]);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    stopPolling();
    stopRecovery();
    if (reconnectToastRef.current) {
      toast.dismiss(reconnectToastRef.current);
      reconnectToastRef.current = null;
    }
  }, [stopPolling, stopRecovery]);

  // Opens (or reopens) the real SSE connection for the current streamId, using
  // whatever abortRef.current is live at call time. Used both by the initial
  // effect below and by attemptRecovery (startRecovery) once a probe confirms
  // SSE is reachable again — reconnecting is the actual recovery action, not
  // just clearing the pollingMode flag (WEBAPP-3S).
  const startStreaming = useCallback(() => {
    const sseUrl = sseUrlRef.current;
    const pollUrl = pollUrlRef.current;
    const signal = abortRef.current.signal;
    connectStreaming(sseUrl, authRef, lastEventIdRef, onEvent, (err) => {
      if (reconnectToastRef.current) {
        toast.dismiss(reconnectToastRef.current);
        reconnectToastRef.current = null;
      }
      if (err.reason === "connect_timeout") {
        // SSE is blocked — switch to polling
        setPollingModeWithRef(true);
        pollingStartRef.current = Date.now();
        Analytics.event(Analytics.EVENTS.STREAMING_FALLBACK, {
          elapsed_ms: CONNECT_TIMEOUT_MS,
        });
        toast.warn("Switched to compatibility mode — real-time updates unavailable on this network", { autoClose: 6000 });
        startRecovery();
        runPolling(pollUrl, signal);
      } else {
        onError(err);
      }
    }, ({ attempt, maxAttempts, delayMs }) => {
      if (reconnectToastRef.current) toast.dismiss(reconnectToastRef.current);
      reconnectToastRef.current = toast.warn(
        `Connection interrupted — reconnecting (${attempt}/${maxAttempts})…`,
        { autoClose: delayMs + 1000 }
      );
    }, signal, () => {
      // SSE succeeded — clear polling cache so next stream starts fresh
      setPollingModeWithRef(false);
    }, onInterChunkTimeout, recordTick, deliveredIdsRef.current);
  }, [onEvent, onError, onInterChunkTimeout, setPollingModeWithRef, startRecovery, runPolling, recordTick]);

  useEffect(() => { startStreamingRef.current = startStreaming; }, [startStreaming]);

  useEffect(() => {
    if (!streamId) {
      abort();
      return;
    }

    pollUrlRef.current = streamURL(streamId, "poll");
    sseUrlRef.current = streamURL(streamId, "sse");
    lastEventIdRef.current = null;
    deliveredIdsRef.current = new Set();
    deliveredIdsRef.current._recentEvents = [];
    abortRef.current = new AbortController();

    if (pollingModeRef.current) {
      // Already in polling mode — skip streaming attempt, go straight to poll
      runPolling(pollUrlRef.current, abortRef.current.signal);
      return () => { abort(); };
    }

    startStreaming();

    return () => { abort(); };
  }, [streamId]);

  useEffect(() => {
    return () => { abort(); };
  }, []);

  return { pollingMode, getLoopHealth };
};
